/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';

import { GlobalStyle } from 'styles/global-styles';

import { LoadingOutlined } from '@ant-design/icons';
import { OverlayThumb } from 'app/components/OverlayThumb/Loadable';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import NotificationsSystem, {
  atalhoTheme,
  dismissNotification,
  setUpNotifications,
} from 'reapop';
import CheckNewVersion from './components/CheckNewVersion';
import { useGlobalFunction } from './hooks/useGlobalFunction';
import { GET_LAYOUT, GET_ME, GET_TRANS, useAppSlice } from './slice';
import {
  selectInfoUser,
  selectIsLogged,
  selectIsLoggedNotEqualUndefined,
  selectLayoutHeader,
  selectMaintenance,
  selectNotifications,
} from './slice/selectors';
import { AppWrapper } from './styled';

import { auth } from 'utils/helper';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import { ConfigProvider } from 'antd';
import en_US from 'antd/lib/locale/en_US';
import vi_VN from 'antd/lib/locale/vi_VN';
import { Layout } from 'app/components/Layout/Loadable';
import DialogNotification from 'app/components/Modals/DialogNotification';
import { NormalRoute, ProtectedRoute } from 'app/components/RouteGuard';
import DialogForgotPass from 'app/pages/App/components/DialogForgotPass';
import { ConfirmPolicyOrder } from 'app/pages/Checkout/ConfirmPolicyOrder';
import { UpdateTicketInfo } from 'app/pages/Checkout/UpdateTicketInfo';
import { NewPaymentCallback } from 'app/pages/NewPaymentCallback';
import { PrintTickets } from 'app/pages/NewPaymentCallback/PrintTickets/Loadable';
import { DeliverPrintTickets } from '../NewPaymentCallback/DeliverPrintTickets/Loadable';
import NotFound from 'app/pages/NotFound';
import PolicyAndTermScreen from 'app/pages/PolicyAndTermScreen';
import { BP_TYPE, TYPE_COOKIE } from 'utils/constants';
import { getCookie } from 'utils/cookies';
import { Agent } from '../Agent/Loadable';
import { Campaign } from '../Campaign/Loadable';
import { Cart } from '../Cart';
import { CatalogDetailShow } from '../Catalog/CatalogDetailShow/Loadable';
import { Catalog } from '../Catalog/Loadable';
import { Home } from '../Home/Loadable';
import PersonalInfo from '../NewAccount/PersonalInfo';
import PurcharseHistory from '../NewAccount/PurchaseHistory';
import CheckTickets from '../NewAccount/CheckTickets';
import { SelectChangeTicket } from '../NewAccount/CheckTickets/SelectChangeTicket/index';
import DetailExchangeTicket from '../NewAccount/CheckTickets/DetailExchangeTicket';
import { DialogChangePass, DialogLogin } from './components/Loadable';
import { Permission } from 'app/hooks/usePermission';
import { LoadingContainerGlobal } from 'app/components/LoadingGlobalScreen';
import EditOrder from '../EditOrder';
import UpdateTicket from '../NewAccount/CheckTickets/UpgradeTickets';
import UpdateSummary from '../NewAccount/CheckTickets/UpgradeSummary';
import TicketRefundSelectedList from '../TicketRefund/pages/TicketSelectedList';
import TicketRefundInfo from '../TicketRefund/pages/TicketRefundInfo';
import TicketRefundSuccess from '../TicketRefund/pages/TicketRefundSuccess';
import TicketRefundDetail from '../TicketRefund/pages/TicketRefundDetail';
import TicketRefundPendingApprove from '../TicketRefund/pages/TicketRefundPendingApprove';
import PDFViewer from '../NewPaymentCallback/PrintTickets/PDFViewer';
import DeliverPDFViewer from '../NewPaymentCallback/DeliverPrintTickets/PDFViewer';
import { ConfirmPayment } from '../NewAccount/CheckTickets/ConfirmPayment';
import DetailTicket, {
  DetailTicketWrapper,
} from 'app/pages/NewAccount/PurchaseHistory/DetailTicket';
import CollectInfoPolicyModal from 'app/pages/App/components/CollectInfoPolicyModal';
import FilesManagement from '../NewAccount/FilesManagement';
import { NcbPayment } from 'app/pages/Checkout/NcbPayment';
import Maintenance from './components/Maintenance';
import MaintenancePage from '../MaintenancePage';
import ApproveOrder from '../NewAccount/ApproveOrder';
import PendingConfirm from '../NewPaymentCallback/PendingConfirm';
import { UnionRepresentative } from '../UnionRepresentative/Loadable';
import AccountPaymentGuide from '../NewAccount/AccountPaymentGuide';
import AutoDeposit from '../NewAccount/AutoDeposit';
import { PaymentHolidayInformation } from '../PaymentHolidayInformation';
import checkPrintTickets from '../NewPaymentCallback/utils/checkPrintTickets';
import checkViewPdf from '../NewPaymentCallback/utils/checkViewPdf';
import checkDeliverPrintTickets from '../NewPaymentCallback/utils/checkDeliverPrintTickets';
import checkDeliverViewPdf from '../NewPaymentCallback/utils/checkDeliverViewPdf';
import TopupHistory from '../TopupHistory';
import { FlightChange } from '../FlightChange/Loadable';
import { BookingMask } from '../BookingMask';
import { HolidayList } from '../HolidayList';
import { HomePage } from '../HomePage';
import { PassengerInfo } from '../PassengerInfo/Loadable';
import { HolidayCheckout } from '../HolidayCheckout';
import { HolidayPaymentCallback } from '../HolidayPaymentCallback';
import { HolidayOrderDetail } from '../HolidayOrderDetail';
import { OrderDetailPage } from '../OrderApproval/OrderDetailPage';
// TicketChange screen removed; TicketList is provided via Drawer in HolidayList
import OrderManagement from '../NewAccount/OrderManagement';
import { useHasComboHolidayAccess } from '../HomePage/hooks/useHasComboHolidayAccess';
// run this function when your application starts before creating any notifications
import BookingSlots from '../NewAccount/BookingSlots';

Object.defineProperty(window, 'getB2bVersion', {
  value: () =>
    `Version: 04.12.25 captcha ${process.env.REACT_APP_RECAPTCHA_KEY ?? ''}`,
  writable: false,
  configurable: false,
});

setUpNotifications({
  defaultProps: {
    allowHTML: true,
    dismissible: true,
    dismissAfter: 2000,
    transition: 'slide',
    position: 'bottom-right',
    showDismissButton: true,
  },
});

export function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const { actions } = useAppSlice();

  const isLogged = useSelector(selectIsLogged);
  const isLoggedNotEqualUndefined = useSelector(
    selectIsLoggedNotEqualUndefined,
  );
  const notifications = useSelector(selectNotifications);
  const infoUser = useSelector(selectInfoUser);
  const header = useSelector(selectLayoutHeader);
  const { data: maintenanceData } = useSelector(selectMaintenance);
  const {
    locale,
    isShowDialogLogin,
    onOpenDialogRegister,
    onCloseDialogAuth,
    onLoginSuccess,
    isShowDialogForgotPass,
    onOpenDialogForgotPass,
    isShowDialogChangePass,
    dialogNotiState,
  } = useGlobalFunction();

  const { accessPrintTicketFromEmailLink } = checkPrintTickets();
  const { accessViewPdfFromEmailLink } = checkViewPdf();
  const { accessDeliverPrintTicketFromEmailLink } = checkDeliverPrintTickets();
  const { accessDeliverViewPdfFromEmailLink } = checkDeliverViewPdf();

  const isProxyBuyer =
    (infoUser?.businessPartner as any)?.bpType === BP_TYPE.PROXY_BUYER;

  useEffect(() => {
    const language = getCookie(TYPE_COOKIE.LANGUAGE) || 'vi';
    dispatch(actions.onChangeLanguage(language));

    dispatch(actions.changeIsLogged({ isLogged: auth() }));
    return () => {
      dispatch(actions.resetState());
    };
  }, [actions, dispatch]);

  useEffect(() => {
    if (
      isLogged ||
      (maintenanceData?.isMaintain &&
        typeof maintenanceData.isMaintain === 'boolean')
    )
      dispatch(GET_LAYOUT());
  }, [dispatch, isLogged, maintenanceData?.isMaintain]);

  useEffect(() => {
    dispatch(GET_TRANS());
  }, [dispatch, isLogged]);

  useEffect(() => {
    if (isLoggedNotEqualUndefined && !maintenanceData?.isMaintain) {
      isLogged && dispatch(GET_ME());
    }
  }, [
    dispatch,
    isLogged,
    isLoggedNotEqualUndefined,
    maintenanceData?.isMaintain,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      console.log = () => {};
    }
  }, []);

  useEffect(() => {
    sessionStorage.removeItem('ticketType');
    sessionStorage.removeItem('prevTicketType');
  }, []);
  const { isHasComboHolidayAccess } = useHasComboHolidayAccess();
  return (
    <ConfigProvider locale={locale === 'vi' ? vi_VN : en_US}>
      <Helmet
        titleTemplate="%s"
        defaultTitle="Sun Tickets"
        title={header?.web_title ?? 'Sun World Ticket Booking'}
        htmlAttributes={{ lang: i18n.language }}
      >
        <meta
          name="description"
          content={header?.web_description ?? 'Sun Tickets'}
        />
        <meta
          name="keywords"
          content={header?.web_keywords ?? 'sunworld, tickets'}
        />
        <meta property="og:locale" content={i18n.language} />
      </Helmet>
      <CheckNewVersion>
        {({ loading, isLatestVersion, refreshCacheAndReload }) => {
          if (loading)
            return (
              <OverlayThumb icon={<LoadingOutlined />} label="Loading..." />
            );
          if (!loading && !isLatestVersion) refreshCacheAndReload();
          return (
            <BrowserRouter>
              <AppWrapper>
                <LoadingContainerGlobal />
                <Layout>
                  {!isLogged ? (
                    <Switch>
                      <Route path="/maintenance" component={MaintenancePage} />
                      {accessPrintTicketFromEmailLink && (
                        <Route
                          path="/print-tickets/:id"
                          component={PrintTickets}
                        />
                      )}
                      {accessViewPdfFromEmailLink && (
                        <Route path="/view-pdf/:id" component={PDFViewer} />
                      )}
                      {accessDeliverPrintTicketFromEmailLink && (
                        <Route
                          path="/deliver-ticket/:lang/:id"
                          component={DeliverPrintTickets}
                        />
                      )}
                      {accessDeliverViewPdfFromEmailLink && (
                        <Route
                          path="/deliver-view-pdf/:id"
                          component={DeliverPDFViewer}
                        />
                      )}
                      <Route path="*" component={Home} />
                    </Switch>
                  ) : (
                    <Switch>
                      <Route path="/maintenance" component={MaintenancePage} />
                      <NormalRoute path="/" exact component={Home} />
                      {/* /flight-change route removed: flight change is now a Drawer inside HolidayList */}

                      {/* /ticket-change route removed — TicketList available via HolidayList Drawer */}
                      <ProtectedRoute
                        path="/checkout/:id"
                        component={ConfirmPolicyOrder}
                      />
                      <ProtectedRoute path="/cart" exact component={Cart} />
                      <ProtectedRoute
                        path="/catalog"
                        exact
                        component={Catalog}
                      />
                      <ProtectedRoute
                        path="/catalog/:id"
                        exact
                        component={CatalogDetailShow}
                      />
                      <ProtectedRoute
                        path="/payment-callback"
                        component={NewPaymentCallback}
                      />
                      <ProtectedRoute
                        path="/pending-confirm"
                        component={PendingConfirm}
                      />

                      <ProtectedRoute
                        path="/print-tickets/:id"
                        component={PrintTickets}
                      />
                      <ProtectedRoute
                        path="/deliver-ticket/:lang/:id"
                        component={DeliverPrintTickets}
                      />
                      <ProtectedRoute
                        path="/deliver-view-pdf/:id"
                        component={DeliverPDFViewer}
                      />
                      <ProtectedRoute
                        path="/view-pdf/:id"
                        component={PDFViewer}
                      />
                      <ProtectedRoute path="/campaign" component={Campaign} />
                      <ProtectedRoute
                        path="/account"
                        component={PurcharseHistory}
                      />
                      <ProtectedRoute
                        path="/order-management"
                        component={OrderManagement}
                      />
                      <ProtectedRoute
                        path="/order/edit/:hashId"
                        component={EditOrder}
                      />
                      <ProtectedRoute
                        path="/policy-term"
                        component={PolicyAndTermScreen}
                      />
                      <ProtectedRoute
                        path="/profile"
                        component={PersonalInfo}
                      />
                      {/* <ProtectedRoute
                            path="/payment-guide"
                            component={AccountPaymentGuide}
                          /> */}
                      <ProtectedRoute
                        path="/update-order-info"
                        component={UpdateTicketInfo}
                      />
                      <ProtectedRoute
                        exact
                        path="/approve-order"
                        component={ApproveOrder}
                      />
                      <ProtectedRoute
                        exact
                        path="/check-tickets"
                        component={CheckTickets}
                      />
                      <ProtectedRoute
                        exact
                        path="/check-tickets/upgrade/:id"
                        component={UpdateTicket}
                      />
                      <ProtectedRoute
                        exact
                        path="/check-tickets/select-tickets/:id"
                        component={SelectChangeTicket}
                      />
                      <ProtectedRoute
                        exact
                        path="/check-tickets/summary/:id"
                        component={UpdateSummary}
                      />
                      <ProtectedRoute
                        exact
                        path="/check-tickets/confirm-policy-order/:id"
                        component={ConfirmPayment}
                      />
                      <ProtectedRoute
                        exact
                        path="/check-tickets/exchange-detail/:id"
                        component={DetailExchangeTicket}
                      />
                      <ProtectedRoute
                        path="/agent"
                        component={
                          !infoUser
                            ? () => null
                            : infoUser?.role_code ===
                                Permission.ROLE_SALE_MANAGER ||
                              infoUser?.role_code === Permission.ROLE_SALE_SHG
                            ? Agent
                            : NotFound
                        }
                      />
                      <ProtectedRoute
                        exact
                        path="/ticket-refund"
                        component={TicketRefundSelectedList}
                      />
                      <ProtectedRoute
                        exact
                        path="/ticket-refund/pending-approve"
                        component={TicketRefundPendingApprove}
                      />
                      <ProtectedRoute
                        exact
                        path="/ticket-refund/info/:idHash"
                        component={TicketRefundInfo}
                      />
                      <ProtectedRoute
                        exact
                        path="/ticket-refund/successfully"
                        component={TicketRefundSuccess}
                      />
                      <ProtectedRoute
                        exact
                        path="/ticket-refund/detail/:idHash"
                        component={TicketRefundDetail}
                      />
                      <ProtectedRoute
                        exact
                        path="/order-detail/:idHash?"
                        component={DetailTicketWrapper}
                      />
                      <ProtectedRoute
                        exact
                        path="/files-management"
                        component={FilesManagement}
                      />
                      <ProtectedRoute
                        path="/ncb-payment"
                        component={NcbPayment}
                      />
                      <ProtectedRoute
                        path="/union-representative"
                        component={UnionRepresentative}
                      />
                      <ProtectedRoute
                        path="/auto-deposit"
                        component={
                          !infoUser
                            ? () => null
                            : infoUser?.role_code ===
                                Permission.ROLE_SALE_MANAGER &&
                              !isProxyBuyer &&
                              !!infoUser.businessPartner?.bankInfo?.tid
                            ? AutoDeposit
                            : NotFound
                        }
                      />
                      <ProtectedRoute
                        path="/topup-history"
                        component={
                          !infoUser
                            ? () => null
                            : infoUser?.role_code ===
                                Permission.ROLE_SALE_MANAGER &&
                              !isProxyBuyer &&
                              !!infoUser.businessPartner?.bankInfo?.tid
                            ? TopupHistory
                            : NotFound
                        }
                      />
                      <ProtectedRoute
                        path="/booking-mask"
                        component={
                          !infoUser
                            ? () => null
                            : isHasComboHolidayAccess
                            ? BookingMask
                            : NotFound
                        }
                      />
                      <ProtectedRoute
                        path="/holiday"
                        component={
                          !infoUser
                            ? () => null
                            : isHasComboHolidayAccess
                            ? HolidayList
                            : NotFound
                        }
                        exact
                      />
                      <ProtectedRoute
                        path="/holiday/passenger-info"
                        component={
                          !infoUser
                            ? () => null
                            : isHasComboHolidayAccess
                            ? PassengerInfo
                            : NotFound
                        }
                        exact
                      />
                      <ProtectedRoute
                        path="/holiday/payment-information"
                        component={
                          !infoUser
                            ? () => null
                            : isHasComboHolidayAccess
                            ? PaymentHolidayInformation
                            : NotFound
                        }
                        exact
                      />
                      <ProtectedRoute
                        path="/holiday/checkout/:id"
                        component={
                          !infoUser
                            ? () => null
                            : isHasComboHolidayAccess
                            ? HolidayCheckout
                            : NotFound
                        }
                      />
                      <ProtectedRoute
                        path="/holiday/payment-callback"
                        component={
                          !infoUser
                            ? () => null
                            : isHasComboHolidayAccess
                            ? HolidayPaymentCallback
                            : NotFound
                        }
                      />
                      <ProtectedRoute
                        path="/holiday/order-detail/:id"
                        component={
                          !infoUser
                            ? () => null
                            : isHasComboHolidayAccess
                            ? HolidayOrderDetail
                            : NotFound
                        }
                      />
                      <ProtectedRoute
                        path="/holiday/approval/:orderCode"
                        component={
                          !infoUser
                            ? () => null
                            : isHasComboHolidayAccess
                            ? OrderDetailPage
                            : NotFound
                        }
                        exact
                      />
                      <ProtectedRoute
                        path="/restaurant/booking-slots"
                        component={BookingSlots}
                      />
                      <ProtectedRoute path="/home" exact component={HomePage} />
                      <Route component={NotFound} />
                      <Redirect to="/" />
                    </Switch>
                  )}
                </Layout>

                <NotificationsSystem
                  theme={atalhoTheme}
                  notifications={notifications}
                  dismissNotification={id => dispatch(dismissNotification(id))}
                />
              </AppWrapper>

              {isShowDialogLogin && (
                <DialogLogin
                  isShow={isShowDialogLogin}
                  onLoginSuccess={onLoginSuccess}
                  onCloseDialog={onCloseDialogAuth}
                  onOpenDialogRegister={onOpenDialogRegister}
                  openDialogForgotPass={onOpenDialogForgotPass}
                />
              )}
              {/* {isShowDialogRegister && (
                    <DialogRegister
                      isShow={isShowDialogRegister}
                      onRegisterSuccess={onLoginSuccess}
                      onCloseDialog={onCloseDialogAuth}
                      onOpenDialogLogin={onOpenDialogLogin}
                    />
                  )} */}
              {isShowDialogForgotPass && (
                <DialogForgotPass
                  isShow={isShowDialogForgotPass}
                  onCloseDialog={onCloseDialogAuth}
                />
              )}
              {isShowDialogChangePass && (
                <DialogChangePass
                  isShow={isShowDialogChangePass}
                  onCloseDialog={onCloseDialogAuth}
                  onSendChangePassDone={() => {}}
                />
              )}
              {dialogNotiState.isShow && (
                <DialogNotification height={'auto'} {...dialogNotiState} />
              )}
              <CollectInfoPolicyModal />
              <Maintenance />
            </BrowserRouter>
          );
        }}
      </CheckNewVersion>
      <GlobalStyle />
    </ConfigProvider>
  );
}
